/**
 * Population Health (Screen G) — PARTIAL/caveated analytics. A left-aligned grid of SkyCard rate tiles,
 * one per population endpoint (immunizations, chronic-control, care-gap-closure, polypharmacy,
 * res-stratification). Each tile fetches its OWN endpoint and is badged from that endpoint's
 * meta.provenance via ProvenanceBadge (never hand-tagged). Because the sandbox is a general FHIR
 * population and NOT a PACE-enrolled cohort, EVERY rate tile surfaces the non-cohort denominator caveat
 * (a true statement about the whole sandbox, rendered as screen copy, not a hand-tagged badge). The
 * metric body is gated on the derived tier: REAL/PARTIAL tiles show the rate; MOCK/DEFERRED tiles show
 * "Awaiting feed" (the dangerous-zero guard), never a synthetic count read as truth. Indicators are
 * framed internally (NPA / DataPACE3); they are NEVER relabelled as MA Stars or HEDIS. Rows are never
 * console.logged; each tile isolates its own loading/error.
 */
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  SkyCard,
  SkyDataGrid,
  SkyDropdown,
  SkyText,
  SkyLoader,
  SkyMessageBar,
  type SkyDataGridColumn,
  type SortDirection,
} from "@skypointcloud/skypoint-ui";
import type {
  ImmunizationRate,
  ChronicControlRow,
  CareGapClosureRow,
  CareGapMemberRow,
  CareGapWorklistSummary,
  ChronicBurdenRow,
  PolypharmacyPrevalence,
  Provenance,
  ResStratificationRow,
} from "@shared/contracts";
import { fetchEnvelope, ApiProblem } from "../lib/api";
import { usePage } from "../lib/usePage";
import { COPY, humanizeToken } from "../lib/copy";
import { deriveBadge } from "../provenance/deriveBadge";
import { ProvenanceBadge } from "../provenance/ProvenanceBadge";
import { PageError } from "../components/PageError";
import { PageHeader } from "../shell/PageHeader";
import { PageFooter } from "../shell/PageFooter";

const PAGE_SIZE = 10;

// Leadership-facing scope boundary: these indicators are CLINICAL, NOT the CMS/HPMS PACE quality
// reporting program. Stated once so no one infers regulatory coverage from this screen.
const CMS_SCOPE_NOTE =
  "These are clinical care-gap indicators, not the CMS/HPMS-required PACE quality reporting program " +
  "(NPA / DataPACE3 plus HPMS Level I and II, IDT per 42 CFR 460.102, enrollment and mortality, " +
  "grievances and appeals), which is out of scope and unbuilt.";

/**
 * Generic population rate tile. The badge is the server-derived provenance; the metric body is gated on
 * the tier (REAL/PARTIAL only). P1-2: the non-cohort caveat is NOT repeated on every tile - it is stated
 * once at the page level + carried by each tile's PARTIAL/REAL* badge, so a structurally-real rate is
 * still never read as an operationally-trusted PACE rate, without burying it under banner-blindness.
 */
function RateTile<T>({
  title,
  description,
  path,
  render,
}: {
  title: string;
  description: string;
  path: string;
  render: (data: T) => ReactNode;
}) {
  const { data, meta, loading, error, retry } = usePage<T>(path);
  const tier = deriveBadge(meta?.provenance).tier;
  const showMetric = tier === "REAL" || tier === "PARTIAL";

  let body: ReactNode;
  if (loading) {
    body = <SkyLoader size="small" label="Loading" />;
  } else if (error) {
    // Fail-forward: the tile's transient error offers a Try-again that re-fires this tile's own fetch.
    body = <PageError title="Something went wrong" onRetry={retry} />;
  } else if (!showMetric || data == null) {
    body = <SkyText size={300}>{COPY.awaitingFeedHolding}</SkyText>;
  } else {
    body = render(data);
  }

  return (
    <SkyCard
      header={{ title, description, action: <ProvenanceBadge meta={meta?.provenance} /> }}
    >
      <div className="flex flex-col gap-3">{body}</div>
    </SkyCard>
  );
}

/** A big single value with a caption, for the REAL/PARTIAL tiles. */
function Metric({ value, caption }: { value: string; caption: string }) {
  return (
    <div className="flex flex-col">
      <SkyText size={700} weight="bold">
        {value}
      </SkyText>
      <SkyText size={200}>{caption}</SkyText>
    </div>
  );
}

/**
 * Whole-percent rate. Only ever called with a real numerator AND a real (> 0) denominator, so the value
 * shown is a genuine rate, never a count read as a percentage. Returns null when no real denominator
 * exists so the caller falls back to an HONEST count tile (DISP honesty: never count-as-rate).
 */
function asRate(numerator: number, denominator: number): string | null {
  if (denominator <= 0) return null;
  return `${Math.round((numerator / denominator) * 100)}% (${numerator} / ${denominator})`;
}

/** "1 group" / "3 groups" — pluralize a count noun so the value is unambiguously a COUNT, not a rate. */
function countLabel(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

/**
 * Shared server-side worklist fetch (page/sort/filter encoded in queryString). Isolates its OWN
 * loading/error so one worklist never blanks the other. Rows are never console.logged (PHI).
 */
function useWorklist<T>(path: string, queryString: string) {
  const [rows, setRows] = useState<T[]>([]);
  const [total, setTotal] = useState(0);
  const [provenance, setProvenance] = useState<Provenance | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // attempt is a fetch dep so retry() can re-fire a transient failure without needing a filter change.
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchEnvelope<T[]>(`${path}?${queryString}`)
      .then(({ data, meta }) => {
        if (cancelled) return;
        // Drift guard: collapse a truthy non-array 200 body to [] so it never reaches the grid's .map().
        setRows(Array.isArray(data) ? data : []);
        if (meta?.total != null) setTotal(meta.total);
        setProvenance(meta?.provenance);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof ApiProblem ? e.problem.title : "Failed to load the worklist.");
        setRows([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [path, queryString, attempt]);

  const retry = useCallback(() => setAttempt((a) => a + 1), []);

  return { rows, total, provenance, loading, error, retry };
}

/** Drill into Member-360 by the non-PHI surrogate key. SPA Link, no reload. */
function memberLink(row: { enterprise_member_key: string; last_name: string | null; first_name: string | null }) {
  return (
    <Link to={`/member/${row.enterprise_member_key}`}>
      {row.last_name ?? "Member"}
      {row.first_name ? `, ${row.first_name}` : ""}
    </Link>
  );
}

/**
 * Worklist 1 — "Members with an open care gap". Member-grain rollup of the REAL/gold open-gap data
 * (same source as the Care Gaps worklist). NO "overdue" wording: an open gap is in-denominator and not
 * in-numerator, never an "overdue" cadence (there is no due-date column to compute one). Domain filter +
 * sort (open-gap count / member) + paginate are all SERVER-side; row drills to Member-360.
 */
function OpenGapMembersSection({ domains }: { domains: string[] }) {
  const [page, setPage] = useState(1);
  const [domain, setDomain] = useState("");
  const [sort, setSort] = useState<string | null>(null);
  const [dir, setDir] = useState<SortDirection | null>(null);

  // Any filter/sort change returns to page 1 so the user never lands on an out-of-range page.
  useEffect(() => {
    setPage(1);
  }, [domain, sort, dir]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (domain) params.set("domain", domain);
    if (sort) params.set("sort", sort);
    if (sort && dir) params.set("dir", dir);
    return params.toString();
  }, [page, domain, sort, dir]);

  const { rows, total, provenance, loading, error, retry } = useWorklist<CareGapMemberRow>(
    "/api/v1/population/care-gap-members",
    queryString,
  );

  // column key -> server sort enum (whitelist; user input never reaches SQL).
  const SORTABLE: Record<string, string> = { last_name: "member", open_gap_count: "gaps" };
  function onSortChange(columnKey: string | null, direction: SortDirection | null) {
    const mapped = columnKey ? SORTABLE[columnKey] : null;
    if (!mapped || !direction) {
      setSort(null);
      setDir(null);
      return;
    }
    setSort(mapped);
    setDir(direction);
  }

  const columns: SkyDataGridColumn<CareGapMemberRow>[] = [
    {
      key: "last_name",
      header: "Member",
      width: "44%",
      sortable: true,
      dataMode: "server",
      renderCell: (_v, row) => memberLink(row),
    },
    {
      key: "open_gap_count",
      header: "Open care gaps",
      width: "28%",
      align: "right",
      sortable: true,
      dataMode: "server",
    },
    {
      key: "top_domain",
      header: "Top domain",
      width: "28%",
      // P2-2: humanize the snake_case domain token; null maps to the "Not recorded" vocabulary word.
      renderCell: (_v, row) => humanizeToken(row.top_domain),
    },
  ];

  return (
    <SkyCard
      header={{
        title: "Members with an open care gap",
        description:
          "Members carrying at least one open care gap (in denominator, not in numerator), by member and " +
          "measure domain only. Row links to Member-360.",
        action: <ProvenanceBadge meta={provenance} />,
      }}
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div className="w-56 max-w-full">
            <SkyDropdown
              label="Domain"
              options={domains.map((d) => ({ value: d, text: humanizeToken(d) }))}
              placeholder="All domains"
              clearable
              selectedOptions={domain ? [domain] : []}
              onOptionSelect={(v) => setDomain(v[0] ?? "")}
            />
          </div>
        </div>
        {error && <PageError title="Could not load the worklist" onRetry={retry} />}
        <SkyDataGrid<CareGapMemberRow>
          data={rows}
          columns={columns}
          getRowId={(r) => r.enterprise_member_key}
          onSortChange={onSortChange}
          pagination
          page={page}
          pageSize={PAGE_SIZE}
          totalItems={total}
          onPageChange={setPage}
          loading={loading}
          emptyMessage="No members with an open care gap on record for the current filter"
        />
      </div>
    </SkyCard>
  );
}

/**
 * Worklist 2 — "Rising chronic burden". Members ranked by chronic_condition_count (multimorbidity). The
 * server verifies the chronic-condition column is populated before ranking; when the feed has not landed
 * it returns DEFERRED, and this section renders an honest "Awaiting chronic-condition feed" holding state
 * (never a fabricated ranking). Sort (count / member) + paginate are SERVER-side; row drills to Member-360.
 */
function ChronicBurdenSection() {
  const THRESHOLD = 3; // multimorbidity floor: members with 3+ chronic conditions
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<string | null>(null);
  const [dir, setDir] = useState<SortDirection | null>(null);

  useEffect(() => {
    setPage(1);
  }, [sort, dir]);

  const queryString = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page),
      pageSize: String(PAGE_SIZE),
      threshold: String(THRESHOLD),
    });
    if (sort) params.set("sort", sort);
    if (sort && dir) params.set("dir", dir);
    return params.toString();
  }, [page, sort, dir]);

  const { rows, total, provenance, loading, error, retry } = useWorklist<ChronicBurdenRow>(
    "/api/v1/population/chronic-burden",
    queryString,
  );
  const tier = deriveBadge(provenance).tier;

  const SORTABLE: Record<string, string> = { last_name: "member", chronic_condition_count: "chronic" };
  function onSortChange(columnKey: string | null, direction: SortDirection | null) {
    const mapped = columnKey ? SORTABLE[columnKey] : null;
    if (!mapped || !direction) {
      setSort(null);
      setDir(null);
      return;
    }
    setSort(mapped);
    setDir(direction);
  }

  const columns: SkyDataGridColumn<ChronicBurdenRow>[] = [
    {
      key: "last_name",
      header: "Member",
      width: "60%",
      sortable: true,
      dataMode: "server",
      renderCell: (_v, row) => memberLink(row),
    },
    {
      key: "chronic_condition_count",
      header: "Chronic conditions",
      width: "40%",
      align: "right",
      sortable: true,
      dataMode: "server",
    },
  ];

  return (
    <SkyCard
      header={{
        title: "Rising chronic burden",
        description: `Members with ${THRESHOLD} or more chronic conditions, highest first. Row links to Member-360.`,
        action: <ProvenanceBadge meta={provenance} />,
      }}
    >
      <div className="flex flex-col gap-3">
        {error && <PageError title="Could not load the worklist" onRetry={retry} />}
        {tier === "DEFERRED" ? (
          <SkyMessageBar
            intent="info"
            title="Awaiting chronic-condition feed"
            message="The chronic-condition count is not populated yet, so this ranking is withheld rather than fabricated. It will populate once the chronic-condition feed lands."
          />
        ) : (
          <SkyDataGrid<ChronicBurdenRow>
            data={rows}
            columns={columns}
            getRowId={(r) => r.enterprise_member_key}
            onSortChange={onSortChange}
            pagination
            page={page}
            pageSize={PAGE_SIZE}
            totalItems={total}
            onPageChange={setPage}
            loading={loading}
            emptyMessage="No members at this chronic-burden threshold on record"
          />
        )}
      </div>
    </SkyCard>
  );
}

/** The actionable worklist layer (A3) — sits ABOVE the honest summary rate tiles. */
function Worklists() {
  // Reuse the gold care-gap summary purely to populate the domain filter (server-derived domain set).
  const summary = usePage<CareGapWorklistSummary>("/api/v1/care-gaps/worklist/stats");
  const domains = useMemo(
    () =>
      [
        ...new Set((summary.data?.byMeasure ?? []).map((r) => r.domain).filter((d): d is string => !!d)),
      ].sort(),
    [summary.data],
  );

  return (
    <div className="flex flex-col gap-4">
      <SkyText size={400} weight="semibold">
        Worklists: who do I need to act on
      </SkyText>
      <SkyText size={200}>
        Not for operational care-management use until the PACE cohort is confirmed on a real ElementCare
        extract. These worklists rank a general FHIR population, not a verified PACE-enrolled panel.
      </SkyText>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <OpenGapMembersSection domains={domains} />
        <ChronicBurdenSection />
      </div>
    </div>
  );
}

export function Population() {
  return (
    <>
      <PageHeader
        crumbs={[
          { key: "home", label: "skyCare" },
          { key: "population", label: "Population Health" },
        ]}
        title="Population Health"
        description="Internal population indicators on a general FHIR sandbox, so every rate is real but its denominator is unproven (not MA Stars or HEDIS)."
      />
      <section className="mt-6 flex flex-1 flex-col gap-4">
        {/* P1-2: the structural non-cohort caveat is stated ONCE here (informational), not on every rate
            tile - each tile's PARTIAL/REAL* badge carries the per-tile reminder without banner-blindness. */}
        <SkyMessageBar
          intent="info"
          title="Denominator unproven"
          message="The sandbox population is a general FHIR set, not a PACE-enrolled cohort. Treat every rate below as PARTIAL: the signal is real but the denominator is unproven."
        />
        <SkyMessageBar
          intent="info"
          title="Not the CMS PACE quality reporting program"
          message={CMS_SCOPE_NOTE}
        />
        <Worklists />
        <SkyText size={400} weight="semibold">
          Population indicators
        </SkyText>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          <RateTile<ImmunizationRate[]>
            title="Immunization rates"
            description="Flu / pneumococcal coverage"
            path="/api/v1/population/immunizations"
            render={(d) => (
              // ImmunizationRate carries a precomputed pct_value per measure but NO numerator/denominator
              // to aggregate into one panel rate, so the tile shows an HONEST COUNT of measures, never a
              // count read as a percentage. Per-measure pct_value is surfaced in detail, not here.
              <Metric
                value={countLabel(d.length, "measure")}
                caption="immunization rate measures reported (count, not a panel rate)"
              />
            )}
          />

          <RateTile<ChronicControlRow[]>
            title="Chronic disease control"
            description="Control rates by chronic condition"
            path="/api/v1/population/chronic-control"
            render={(d) => {
              // Real rate: SUM numerator / SUM denominator across measure groups (both are real columns
              // from op_fact_quality_measure). Denominator visible. Falls back to an honest count if no
              // denominator has landed yet.
              const numerator = d.reduce((s, r) => s + r.numerator, 0);
              const denominator = d.reduce((s, r) => s + r.denominator, 0);
              const rate = asRate(numerator, denominator);
              return rate ? (
                <Metric value={rate} caption={`controlled across ${countLabel(d.length, "measure group")}`} />
              ) : (
                <Metric
                  value={countLabel(d.length, "measure group")}
                  caption="chronic-control measure group(s) (denominator not yet reported)"
                />
              );
            }}
          />

          <RateTile<CareGapClosureRow[]>
            title="Care-gap closure"
            description="Closure rollup across the panel"
            path="/api/v1/population/care-gap-closure"
            render={(d) => {
              // Real rate: SUM closed / SUM(closed + open) across rollup rows. Denominator visible. Falls
              // back to an honest count if no open/closed denominator has landed yet.
              const closed = d.reduce((s, r) => s + r.closed, 0);
              const denominator = d.reduce((s, r) => s + r.closed + r.open, 0);
              const rate = asRate(closed, denominator);
              return rate ? (
                <Metric value={rate} caption={`gaps closed across ${countLabel(d.length, "rollup row")}`} />
              ) : (
                <Metric
                  value={countLabel(d.length, "rollup row")}
                  caption="care-gap closure rollup row(s) (no open/closed denominator yet)"
                />
              );
            }}
          />

          <RateTile<PolypharmacyPrevalence>
            title="Polypharmacy prevalence"
            description="Participants on five or more medications"
            path="/api/v1/population/polypharmacy"
            render={(d) => (
              // Count only: there is no panel denominator, so this is an HONEST member COUNT, never a
              // "prevalence rate". The threshold makes the count self-describing.
              <Metric
                value={countLabel(d.count, "participant")}
                caption={`at ${d.threshold}+ medications (count, no panel denominator)`}
              />
            )}
          />

          <RateTile<ResStratificationRow[]>
            title="Race / ethnicity stratification"
            description="Equity stratification of the panel"
            path="/api/v1/population/res-stratification?dimension=race"
            render={(d) => (
              // Honest COUNT of stratification groups (per-group pct lives in the group breakdown, not as a
              // single tile rate). Never a count read as a percentage.
              <Metric
                value={countLabel(d.length, "group")}
                caption="race / ethnicity stratification group(s) (count)"
              />
            )}
          />
        </div>
      </section>
      <PageFooter />
    </>
  );
}
